import type {
	Catalog,
	CatalogBrand,
	CatalogCategory,
	CatalogKeyword,
} from "@sobidogam/domain";
import brandsData from "../data/brands.json";
import categoriesData from "../data/categories.json";
import groupsData from "../data/groups.json";
import keywordsData from "../data/keywords.json";
import symbolsData from "../data/symbols.json";

export interface CatalogGroup {
	groupId: string;
	nameKo: string;
	sortOrder: number;
	colorToken: string;
}

export interface CatalogSymbol {
	symbolId: string;
	file: string;
}

export const groups = groupsData as CatalogGroup[];
export const categories = categoriesData as CatalogCategory[];
export const brands = brandsData as CatalogBrand[];
export const keywords = keywordsData as CatalogKeyword[];
export const symbols = symbolsData as CatalogSymbol[];
export const catalog: Catalog = { categories, brands, keywords };

export function categoryBySlug(slug: string): CatalogCategory | undefined {
	return categories.find((category) => category.slug === slug);
}

export function groupById(groupId: string): CatalogGroup | undefined {
	return groups.find((group) => group.groupId === groupId);
}

export function validateCatalog(): string[] {
	const errors: string[] = [];
	const unique = (values: string[], label: string) => {
		const seen = new Set<string>();
		for (const value of values) {
			if (seen.has(value)) errors.push(`${label} duplicate: ${value}`);
			seen.add(value);
		}
	};
	if (categories.length !== 128)
		errors.push(
			`categories must contain 128 entries (got ${categories.length})`,
		);
	if (groups.length !== 12)
		errors.push(`groups must contain 12 entries (got ${groups.length})`);
	unique(
		categories.map((category) => category.categoryId),
		"categoryId",
	);
	unique(
		categories.map((category) => category.slug),
		"slug",
	);
	unique(
		brands.map((brand) => brand.brandId),
		"brandId",
	);
	unique(
		brands.flatMap((brand) => brand.aliases),
		"brand alias",
	);
	const groupIds = new Set(groups.map((group) => group.groupId));
	const categoryIds = new Set(
		categories.map((category) => category.categoryId),
	);
	const symbolIds = new Set(symbols.map((symbol) => symbol.symbolId));
	for (const category of categories) {
		if (!groupIds.has(category.groupId))
			errors.push(
				`category ${category.categoryId} references missing group ${category.groupId}`,
			);
		if (!symbolIds.has(category.symbolId))
			errors.push(
				`category ${category.categoryId} references missing symbol ${category.symbolId}`,
			);
		if (!/^[a-z0-9-]+$/.test(category.slug))
			errors.push(`category slug is not URL safe: ${category.slug}`);
		if (!category.descKo?.trim())
			errors.push(`category ${category.categoryId} has no description`);
	}
	for (const brand of brands) {
		if (!categoryIds.has(brand.categoryId))
			errors.push(
				`brand ${brand.brandId} references missing category ${brand.categoryId}`,
			);
		if (!/^#[0-9a-f]{6}$/i.test(brand.colorHex))
			errors.push(`brand ${brand.brandId} has invalid colorHex`);
	}
	for (const rule of keywords)
		if (!categoryIds.has(rule.categoryId))
			errors.push(
				`keyword ${rule.pattern} references missing category ${rule.categoryId}`,
			);
	if (categories.filter((category) => category.sensitive).length !== 5)
		errors.push("exactly five sensitive categories are required");
	return errors;
}

export type { Catalog, CatalogBrand, CatalogCategory, CatalogKeyword };
